import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import store from "store/store";
import { budgetBuilderSelectors } from "store/selectors";
import { MEMBER_TYPES } from "globals/constants";
import { findUnitById, getResearchTeamRoles } from "utils";
import { convertToLookupTable, toNumber } from "utils/budgetBuilder";

import { saveBudgetBuilderForm, updateResearchTeam } from "../budgetBuilder";
import { buildUpLookupList, filterAndChangeMembersType, findRoleInRoles } from "./utils";
import { api } from "services";


export const isNumberGreaterThanZero = number => !isNaN(number) && number > 0;

export const addNewResearchTeamMember = async userData => {
	const state = store.getState();
	const researchTeam = cloneDeep(budgetBuilderSelectors.researchTeamMembers(state));
	const unnamedMembers = cloneDeep(budgetBuilderSelectors.unnamedMembers(state));

	const associations = [
		{
			id: userData.association,
			calendarMonths: userData.calendarMonths,
			calendarSalary: userData.calendarSalary,
			calendarFringeRate: userData.calendarFringeRate,
			academicMonths: userData.academicMonths,
			academicSalary: userData.academicSalary,
			academicFringeRate: userData.academicFringeRate,
			summerMonths: userData.summerMonths,
			summerSalary: userData.summerSalary,
			summerFringeRate: userData.summerFringeRate,
		},
	];
	const baseMember = {
		association: userData.association,
		associations,
		role: userData.role,
		type: MEMBER_TYPES.MEMBER_UNNAMED,
	};

	if (isNumberGreaterThanZero(userData.summerMonths)) {
		unnamedMembers.push({
			...baseMember,
			id: uuidv4(),
			baseSalary: userData.summerSalary,
			reqSalary: userData.summerSalary,
			apptMonths: userData.summerMonths + " - S",
			fringeRate: userData.summerFringeRate,
		});
	}
	if (isNumberGreaterThanZero(userData.calendarMonths)) {
		unnamedMembers.push({
			...baseMember,
			id: uuidv4(),
			baseSalary: userData.calendarSalary,
			reqSalary: userData.calendarSalary,
			apptMonths: userData.calendarMonths + " - C",
			fringeRate: userData.calendarFringeRate,
		});
	}
	if (isNumberGreaterThanZero(userData.academicMonths)) {
		unnamedMembers.push({
			...baseMember,
			id: uuidv4(),
			baseSalary: userData.academicSalary,
			reqSalary: userData.academicSalary,
			apptMonths: userData.academicMonths + " - A",
			fringeRate: userData.academicFringeRate,
		});
	}

	updateResearchTeam(researchTeam, unnamedMembers);

	return saveBudgetBuilderForm();
};

const createBaseMember = (member, person, displayRole, effort, personMonths) => ({
	member: member.member, // we can't use existing id's, since it's not enforced that a research team assignment exists exactly once
	name: person.name,
	role: member.role,
	roleLabel: displayRole,
	associations: member.associations,
	effort,
	personMonths,
	fringeRate: 0,
	fringeRequested: member.fringeRequested,
	type: member.type,
	resolved: true,
});

const toAssociationsById = associations => {
	const associationsById = {};

	if (Array.isArray(associations)) {
		for (const association of associations) {
			associationsById[association.id] = association;
		}
	}

	return associationsById;
};

export const createMembersFromAssociations = (
	person,
	member,
	displayRole,
	sortedTeam,
) => {
	const associationsById = toAssociationsById(person.associations);
	const baseMember = createBaseMember(
		member,
		person,
		displayRole,
		toNumber(member.effort),
		toNumber(member.personMonths),
	);

	for (const memberAssociation of member.associations) {
		const assoc = associationsById[memberAssociation.association] || {};
		const hasSummerData = assoc.isSummerAppointment && assoc.summerMonths && assoc.summerMonths > 0;
		const hasAppointmentData = assoc.isAcademicAppointment && assoc.academicMonths && assoc.academicMonths > 0;

		if (hasSummerData || hasAppointmentData) {
			if (hasSummerData) {
				sortedTeam.push({
					...baseMember,
					id: uuidv4(),
					association: assoc.id,
					associationLabel: assoc.name,
					baseSalary: assoc.summerSalary,
					reqSalary: assoc.summerSalary,
					apptMonths: `${assoc.summerMonths} - S`,
				});
			}
			if (hasAppointmentData) {
				sortedTeam.push({
					...baseMember,
					id: uuidv4(),
					association: assoc.id,
					associationLabel: assoc.name,
					baseSalary: assoc.academicSalary,
					reqSalary: assoc.academicSalary,
					apptMonths: `${assoc.academicMonths} - A`,
				});
			}
		} else if (assoc.isCalendarAppointment && assoc.calendarMonths && assoc.calendarMonths > 0) {
			sortedTeam.push({
				...baseMember,
				id: uuidv4(),
				association: assoc.id,
				associationLabel: assoc.name,
				baseSalary: assoc.calendarSalary,
				reqSalary: assoc.calendarSalary,
				apptMonths: `${assoc.calendarMonths} - C`,
			});
		} else {
			sortedTeam.push({
				...baseMember,
				id: uuidv4(),
				association: assoc.id,
				associationLabel: assoc.name,
				baseSalary: 0,
				reqSalary: 0,
				apptMonths: "12 - C",
			});
		}
	}
};

const collectUnnamedMembers = async (unnamedMembers, roles) => {
	const members = [];

	const units = await Promise.allSettled(unnamedMembers.map(m => findUnitById(m.association)));

	const unitsById = units.reduce((acc, promise) => {
		if (promise.status === "fulfilled") {
			acc[promise.value.id] = promise.value.name;
		}

		return acc;
	}, {});

	const rolesById = {};
	for (const role of roles) {
		rolesById[role.id] = role.label;
	}

	for (const unnamedMember of unnamedMembers) {
		members.push({
			id: uuidv4(),
			...unnamedMember,
			roleLabel: rolesById[unnamedMember.role],
			association: unnamedMember.association,
			associationLabel: unitsById[unnamedMember.association] || "",
			resolved: true,
			isResearchTeamMember: true,
			type: MEMBER_TYPES.MEMBER_UNNAMED,
		});
	}

	return members
};


export const collectResearchTeamDataForPeriod = async (
	budgetBuilderMembers,
	parentMembers,
	unnamedMembers,
	cachedPeopleById,
	currentPeriodNumber,
) => {
	let researchTeam = [];
	const lookupMap = buildUpLookupList(budgetBuilderMembers);
	const parentMemberLookupMap = convertToLookupTable(parentMembers, "member");

	if (isNonEmptyArray(parentMembers)) {
		researchTeam = filterAndChangeMembersType(parentMembers, budgetBuilderMembers);
	}

	if (isNonEmptyArray(budgetBuilderMembers)) {
		for (const member of budgetBuilderMembers) {
			if (member.type === MEMBER_TYPES.MEMBER_PARENT && parentMemberLookupMap?.[member.member]) {
				researchTeam.push({
					...member,
					...parentMemberLookupMap[member.member],
				});
			} else if (member.type === MEMBER_TYPES.MEMBER_BUDGET_BUILDER) {
				researchTeam.push(member);
			}
		}
	}

	if (isNonEmptyArray(unnamedMembers)) {
		for (const member of unnamedMembers) {
			const notIncluded = Array.isArray(member.excludedFromPeriods) && !member.excludedFromPeriods.includes(currentPeriodNumber);

			if (!member?.excludedFromPeriods || notIncluded) {
				researchTeam.push({
					...member,
					...lookupMap?.[member.id],
					type: MEMBER_TYPES.MEMBER_UNNAMED,
				});
			}
		}
	}

	return collectResearchTeamData(researchTeam, cachedPeopleById);
};

const collectResearchTeamData = async (teamMembers, cachedPeopleById) => {
	let sortedTeam = [];
	const teamMemberPromises = [];
	const memberById = {};
	const unnamedMembers = [];
	let newCachedPeopleById = { ...cachedPeopleById };

	const roles = await getResearchTeamRoles();

	if (isNonEmptyArray(teamMembers)) {
		if (teamMembers !== null) {
			for (const teamMember of teamMembers) {
				const memberId = teamMember.member;
				if (teamMember.type === MEMBER_TYPES.MEMBER_UNNAMED) {
					unnamedMembers.push(teamMember);
				} else if (teamMember.resolved) {
					sortedTeam.push({ ...teamMember, id: teamMember.id || uuidv4() });
				} else if (memberId in cachedPeopleById) {
					createMembersFromAssociations(
						cachedPeopleById[memberId] || teamMember,
						teamMember,
						findRoleInRoles(teamMember.role, roles),
						sortedTeam,
					);
				} else {
					memberById[memberId] = teamMember;
					if (memberId) {
						teamMemberPromises.push(api.person.get({ id: memberId }));
					}
				}
			}
		}

		const response = await Promise.all(teamMemberPromises);

		if (isNonEmptyArray(response)) {
			for (const person of response) {
				const member = memberById[person.id];
				const role = findRoleInRoles(member.role, roles);

				newCachedPeopleById[person.id] = person;

				if (!member?.resolved) {
					createMembersFromAssociations(person, member, role, sortedTeam);
				}
			}
		}

		const updatedUnnamedMembers = await collectUnnamedMembers(unnamedMembers, roles);
		sortedTeam = sortedTeam.concat(updatedUnnamedMembers);
	}

	return {
		researchTeam: {
			members: sortedTeam,
			unnamed: unnamedMembers,
		},
		cachedPeopleById: newCachedPeopleById,
	};
};
